"use client";

import { useCallback, useContext, useMemo } from "react";
import { IEventFeedResponse, ITopLeagueFeedResponse } from "@finbackoffice/websocket-client";
import { sortAlphabeticallyByProp, sortByScheduledTime } from "@finbackoffice/fe-core";
import { convertGamesToSports, convertGamesToTournaments } from "@finbackoffice/site-core";
import { ISportFeedResponse } from "@finbackoffice/websocket-client";
import { ConfigContextV2 } from "contexts";

interface IUseSportDataFromEventsOptions {
    sortTournaments?: boolean;
}

interface IUseSportDataFromEventsResult {
    sports: ISportFeedResponse[];
    activeSportId: number | undefined;
    activeSportEvents: IEventFeedResponse[];
    activeSportTournaments: ITopLeagueFeedResponse[];
    getGamesByTournamentId: (id: number) => IEventFeedResponse[];
}

export const useSportDataFromEvents = (
    eventsData: IEventFeedResponse[],
    sportSlug: string | undefined,
    options?: IUseSportDataFromEventsOptions,
): IUseSportDataFromEventsResult => {
    const { sportsOrder } = useContext(ConfigContextV2);

    const sports = useMemo(
        () => convertGamesToSports(eventsData, sportsOrder),
        [eventsData, sportsOrder],
    );

    const activeSportId = useMemo(
        () => sports.find((sport) => sport.slug === sportSlug)?.id ?? sports[0]?.id,
        [sports, sportSlug],
    );

    const activeSportEvents = useMemo(
        () =>
            eventsData
                .filter((event) => event.sport_id === activeSportId)
                .sort(sortByScheduledTime),
        [activeSportId, eventsData],
    );

    const activeSportTournaments = useMemo(() => {
        const tournaments = convertGamesToTournaments(activeSportEvents);
        return options?.sortTournaments
            ? tournaments.sort(sortAlphabeticallyByProp("top_index"))
            : tournaments;
    }, [activeSportEvents, options?.sortTournaments]);

    const gamesByTournamentId = useMemo(() => {
        const map = new Map<number, IEventFeedResponse[]>();
        for (const event of activeSportEvents) {
            const bucket = map.get(event.tournament_id);
            bucket ? bucket.push(event) : map.set(event.tournament_id, [event]);
        }
        return map;
    }, [activeSportEvents]);

    const getGamesByTournamentId = useCallback(
        (id: number) => gamesByTournamentId.get(id) ?? [],
        [gamesByTournamentId],
    );

    return {
        sports,
        activeSportId,
        activeSportEvents,
        activeSportTournaments,
        getGamesByTournamentId,
    };
};
